<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const props = defineProps({
  device: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['refresh']);

const { t } = useI18n();
const store = useStore();

// Menú de acciones
const showActionsMenu = ref(false);
const isEditing = ref(false);
const isDeleting = ref(false);
const showDeleteConfirm = ref(false);
const editedName = ref('');

const handleEdit = () => {
  editedName.value = props.device.name;
  isEditing.value = true;
  showActionsMenu.value = false;
};

const handleCancelEdit = () => {
  isEditing.value = false;
  editedName.value = '';
};

const handleSaveEdit = async () => {
  if (!editedName.value.trim()) {
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.NAME_REQUIRED'), 'error');
    return;
  }

  try {
    await store.dispatch('buhotextCampaigns/updateDevice', {
      deviceId: props.device.id,
      name: editedName.value.trim(),
    });
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.UPDATE_SUCCESS'));
    isEditing.value = false;
    emit('refresh');
  } catch (error) {
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.UPDATE_ERROR'), 'error');
  }
};

const openDeleteConfirm = () => {
  showDeleteConfirm.value = true;
  showActionsMenu.value = false;
};

const handleDelete = async () => {
  isDeleting.value = true;
  try {
    await store.dispatch('buhotextCampaigns/deleteDevice', props.device.id);
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.DELETE_SUCCESS'));
    showDeleteConfirm.value = false;
    emit('refresh');
  } catch (error) {
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.DELETE_ERROR'), 'error');
  } finally {
    isDeleting.value = false;
  }
};

// Obtener API Key y Site URL desde la configuración del servidor BuhoText
const account = useMapGetter('getCurrentAccount');
const apiKey = computed(() => {
  // Obtener desde BuhotextServer (configurado en superadmin)
  return (
    account.value?.custom_attributes?.buhotext_api_key ||
    '93a9916b9cda62480b95a35b325750cd'
  );
});

const siteUrl = computed(() => {
  // URL completa de 66text con protocolo
  return 'https://bt.buho.xyz/';
});

const copyToClipboard = async text => {
  try {
    await navigator.clipboard.writeText(text);
    useAlert(t('CAMPAIGN.BUHOTEXT.DEVICES.COPIED'));
  } catch (error) {
    useAlert('Failed to copy', 'error');
  }
};

// Según API 66text: device tiene last_ping_datetime para determinar si está online
const isOnline = computed(() => {
  if (!props.device.last_ping_datetime) return false;
  const lastPing = new Date(props.device.last_ping_datetime);
  const now = new Date();
  const diffMinutes = (now - lastPing) / 1000 / 60;
  return diffMinutes < 5; // Online si hizo ping en últimos 5 minutos
});

const statusClass = computed(() => {
  return isOnline.value
    ? 'bg-n-green-3 text-n-green-11 border border-n-green-6'
    : 'bg-n-slate-3 text-n-slate-11 border border-n-slate-6';
});

const statusLabel = computed(() => {
  return isOnline.value
    ? t('CAMPAIGN.BUHOTEXT.DEVICES.STATUS.ONLINE')
    : t('CAMPAIGN.BUHOTEXT.DEVICES.STATUS.OFFLINE');
});

const batteryIcon = computed(() => {
  const level = props.device.device_battery || 0;
  if (level > 80) return 'i-lucide-battery-full';
  if (level > 50) return 'i-lucide-battery-medium';
  if (level > 20) return 'i-lucide-battery-low';
  return 'i-lucide-battery';
});

const batteryColor = computed(() => {
  const level = props.device.device_battery || 0;
  if (props.device.device_is_charging) return 'text-n-green-11';
  if (level > 20) return 'text-n-slate-11';
  return 'text-n-red-11';
});

const simCards = computed(() => {
  return props.device.sims || [];
});
</script>

<template>
  <div
    class="p-4 rounded-xl border border-n-weak bg-n-alpha-3 hover:border-n-blue-9 transition-all"
  >
    <div class="flex items-start justify-between mb-3">
      <div class="flex items-center gap-3 flex-1">
        <div
          class="w-12 h-12 rounded-full bg-n-purple-3 flex items-center justify-center flex-shrink-0"
        >
          <i class="i-lucide-smartphone text-xl text-n-purple-11" />
        </div>
        <div class="flex-1 min-w-0">
          <template v-if="!isEditing">
            <h4 class="font-semibold text-n-slate-12">
              {{ device.name }}
            </h4>
            <p class="text-xs text-n-slate-11">
              {{ device.device_brand }} {{ device.device_model }}
            </p>
          </template>
        </div>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0">
        <span
          :class="statusClass"
          class="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
        >
          <span class="w-2 h-2 rounded-full bg-current" />
          {{ statusLabel }}
        </span>

        <!-- Menú de acciones -->
        <div class="relative">
          <button
            class="p-1.5 rounded hover:bg-n-slate-3 text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click="showActionsMenu = !showActionsMenu"
          >
            <i class="i-lucide-more-vertical text-lg" />
          </button>

          <div
            v-if="showActionsMenu"
            class="absolute right-0 mt-1 w-40 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-lg shadow-lg z-10"
          >
            <button
              class="w-full px-3 py-2 text-left text-sm hover:bg-n-slate-3 flex items-center gap-2 text-n-slate-12 first:rounded-t-lg"
              @click="handleEdit"
            >
              <i class="i-lucide-pencil text-base" />
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.EDIT') }}
            </button>
            <button
              class="w-full px-3 py-2 text-left text-sm hover:bg-n-red-3 flex items-center gap-2 text-n-red-11 last:rounded-b-lg"
              :disabled="isDeleting"
              @click="openDeleteConfirm"
            >
              <i class="i-lucide-trash-2 text-base" />
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modo edición -->
    <div v-if="isEditing" class="mb-3 pb-3 border-b border-n-weak">
      <div class="flex gap-2">
        <input
          v-model="editedName"
          type="text"
          class="flex-1 px-3 py-2 border border-n-weak rounded-lg text-sm bg-n-slate-2 text-n-slate-12 focus:outline-none focus:border-n-blue-9"
          :placeholder="t('CAMPAIGN.BUHOTEXT.DEVICES.EDIT_PLACEHOLDER')"
          @keyup.enter="handleSaveEdit"
          @keyup.esc="handleCancelEdit"
        />
        <button
          class="px-3 py-2 rounded-lg border border-n-weak bg-n-green-3 hover:bg-n-green-4 text-n-green-11 transition-colors"
          @click="handleSaveEdit"
        >
          <i class="i-lucide-check text-base" />
        </button>
        <button
          class="px-3 py-2 rounded-lg border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 transition-colors"
          @click="handleCancelEdit"
        >
          <i class="i-lucide-x text-base" />
        </button>
      </div>
    </div>

    <div class="space-y-2 text-sm">
      <div class="flex items-center justify-between text-n-slate-11">
        <span>{{ t('CAMPAIGN.BUHOTEXT.DEVICES.ID') }}</span>
        <code
          class="text-xs bg-n-slate-3 px-2 py-1 rounded text-n-slate-12 font-mono"
        >
          {{
            t('CAMPAIGN.BUHOTEXT.DEVICES.DEVICE_NUMBER', { number: device.id })
          }}
        </code>
      </div>

      <div
        v-if="device.device_battery !== null"
        class="flex items-center justify-between text-n-slate-11"
      >
        <span>{{ t('CAMPAIGN.BUHOTEXT.DEVICES.BATTERY') }}</span>
        <div class="flex items-center gap-1.5" :class="batteryColor">
          <i :class="batteryIcon" class="text-base" />
          <span>{{
            t('CAMPAIGN.BUHOTEXT.DEVICES.BATTERY_PERCENT', {
              percent: device.device_battery,
            })
          }}</span>
          <i
            v-if="device.device_is_charging"
            class="i-lucide-zap text-xs text-n-yellow-11"
          />
        </div>
      </div>

      <div v-if="simCards.length > 0" class="space-y-1">
        <div class="text-n-slate-11 text-xs font-medium mb-1">
          {{ t('CAMPAIGN.BUHOTEXT.DEVICES.SIMS') }}
        </div>
        <div
          v-for="sim in simCards"
          :key="sim.subscription_id"
          class="flex items-center justify-between text-xs bg-n-slate-2 px-2 py-1.5 rounded"
        >
          <div class="flex items-center gap-2">
            <i class="i-lucide-sim-card text-sm text-n-blue-11" />
            <span class="text-n-slate-12 font-medium">{{
              sim.carrier_name
            }}</span>
          </div>
          <span class="text-n-slate-11">{{ sim.phone_number }}</span>
        </div>
      </div>

      <!-- Configuración para APP -->
      <div class="space-y-2 pt-2 border-t border-n-weak">
        <div class="text-n-slate-11 text-xs font-medium mb-1">
          {{ t('CAMPAIGN.BUHOTEXT.DEVICES.APP_CONFIG') }}
        </div>

        <div class="space-y-1.5">
          <div class="space-y-1">
            <label class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.API_KEY') }}
            </label>
            <div class="flex gap-1">
              <input
                :value="apiKey"
                readonly
                class="flex-1 px-2 py-1 border border-n-weak rounded text-xs bg-n-slate-2 text-n-slate-12 font-mono"
              />
              <button
                class="px-2 py-1 rounded border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                @click="copyToClipboard(apiKey)"
              >
                <i class="i-lucide-copy text-xs" />
              </button>
            </div>
          </div>

          <div class="space-y-1">
            <label class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.SITE_URL') }}
            </label>
            <div class="flex gap-1">
              <input
                :value="siteUrl"
                readonly
                class="flex-1 px-2 py-1 border border-n-weak rounded text-xs bg-n-slate-2 text-n-slate-12 font-mono"
              />
              <button
                class="px-2 py-1 rounded border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                @click="copyToClipboard(siteUrl)"
              >
                <i class="i-lucide-copy text-xs" />
              </button>
            </div>
          </div>

          <div class="space-y-1">
            <label class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.DEVICE_ID') }}
            </label>
            <div class="flex gap-1">
              <input
                :value="device.id"
                readonly
                class="flex-1 px-2 py-1 border border-n-weak rounded text-xs bg-n-slate-2 text-n-slate-12 font-mono"
              />
              <button
                class="px-2 py-1 rounded border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                @click="copyToClipboard(device.id.toString())"
              >
                <i class="i-lucide-copy text-xs" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-2 pt-2 border-t border-n-weak text-xs">
        <div class="text-center">
          <div class="text-n-slate-11">
            {{ t('CAMPAIGN.BUHOTEXT.DEVICES.STATS.SENT') }}
          </div>
          <div class="text-n-slate-12 font-semibold">
            {{ device.total_sent_sms || 0 }}
          </div>
        </div>
        <div class="text-center">
          <div class="text-n-slate-11">
            {{ t('CAMPAIGN.BUHOTEXT.DEVICES.STATS.PENDING') }}
          </div>
          <div class="text-n-slate-12 font-semibold">
            {{ device.total_pending_sms || 0 }}
          </div>
        </div>
      </div>

      <div
        v-if="device.last_ping_datetime"
        class="flex items-center justify-between text-xs text-n-slate-11 pt-2"
      >
        <span>{{ t('CAMPAIGN.BUHOTEXT.DEVICES.LAST_PING') }}</span>
        <span>{{ new Date(device.last_ping_datetime).toLocaleString() }}</span>
      </div>
    </div>

    <!-- Modal de confirmación de eliminación -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      @click.self="showDeleteConfirm = false"
    >
      <div
        class="w-full max-w-md m-4 bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-xl"
      >
        <div class="p-6">
          <div class="flex items-start gap-4 mb-4">
            <div
              class="w-12 h-12 rounded-full bg-n-red-3 flex items-center justify-center flex-shrink-0"
            >
              <i class="i-lucide-alert-triangle text-2xl text-n-red-11" />
            </div>
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                {{ t('CAMPAIGN.BUHOTEXT.DEVICES.DELETE_CONFIRM_TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{
                  t('CAMPAIGN.BUHOTEXT.DEVICES.DELETE_CONFIRM', {
                    name: device.name,
                  })
                }}
              </p>
            </div>
          </div>

          <div class="flex gap-3 justify-end">
            <button
              class="px-4 py-2 rounded-lg border border-n-weak bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-12 transition-colors"
              @click="showDeleteConfirm = false"
            >
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 rounded-lg bg-n-red-9 hover:bg-n-red-10 text-white transition-colors flex items-center gap-2"
              :disabled="isDeleting"
              @click="handleDelete"
            >
              <i v-if="isDeleting" class="i-lucide-loader-2 animate-spin" />
              <i v-else class="i-lucide-trash-2" />
              {{ t('CAMPAIGN.BUHOTEXT.DEVICES.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
